import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageSquare, Loader2, Check, XCircle, Users } from "lucide-react";
import { api } from "@/lib/api";

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

export default function BalancePay() {
  const { sessionId } = useParams();
  const [res, setRes] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | paid | cancelled | invalid

  useEffect(() => {
    api.get(`/sections/balance/${sessionId}`)
      .then(({ data }) => {
        setRes(data);
        setState(data.balance_paid ? "paid" : data.cancelled ? "cancelled" : "ready");
      })
      .catch(() => setState("invalid"));
  }, [sessionId]);

  const textHref = res
    ? `sms:+13239261750?&body=${encodeURIComponent(`Hi! I'd like to Apple Pay my section balance for ${res.section_name} — order ${res.reservation_no || ""}`)}`
    : "#";

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6" data-testid="balance-page">
      <div className="grain-overlay" />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md border border-white/10 bg-black/40 backdrop-blur-xl p-10 text-center gold-glow">
        <div className="flex justify-center mb-3">
          <img src="/logo-jayatk.png" alt="Jay Atk" className="h-9 w-auto" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#8C7324] mb-8">Bottle Balance</div>

        {state === "loading" && <Loader2 className="w-8 h-8 text-[#D4AF37] mx-auto animate-spin" data-testid="balance-loading" />}

        {state === "invalid" && (
          <div data-testid="balance-invalid">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-5" />
            <h1 className="font-serif-display text-3xl text-[#FDFBF7]">Reservation not found</h1>
            <p className="text-[#A19D94] mt-3 text-sm">Double-check the link from your email or text, or reach out to Jay's team.</p>
          </div>
        )}

        {state === "cancelled" && (
          <div data-testid="balance-cancelled">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-5" />
            <h1 className="font-serif-display text-3xl text-[#FDFBF7]">This reservation was cancelled</h1>
            <p className="text-[#A19D94] mt-3 text-sm">Reach out to Jay's team if you think this is a mistake.</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="balance-already-paid">
            <Check className="w-12 h-12 text-green-400 mx-auto mb-5" />
            <h1 className="font-serif-display text-3xl text-[#FDFBF7]">All paid — see you on the 4th</h1>
            <p className="text-[#A19D94] mt-3 text-sm">{res?.section_name} is 100% locked in. Nothing left to do but pick your fit.</p>
          </div>
        )}

        {state === "ready" && res && (
          <div data-testid="balance-ready">
            <h1 className="font-serif-display text-3xl text-[#FDFBF7] leading-tight" data-testid="balance-section-name">{res.section_name}</h1>
            <div className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#A19D94] mt-2">
              <Users className="w-3.5 h-3.5 text-[#D4AF37]" /> Party of {res.party_size}{res.name ? ` · ${res.name}` : ""}
            </div>
            <p className="text-[11px] text-[#5c5850] mt-1">{res.event}</p>

            <div className="mt-7 border border-white/10 bg-black/50 text-left" data-testid="balance-bottles">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#8C7324] px-5 pt-4 pb-2 border-b border-white/10">Your bottles (mixers included)</div>
              {res.bottles.map((b) => (
                <div key={b.id} className="flex justify-between px-5 py-2.5 text-sm" data-testid={`balance-bottle-${b.id}`}>
                  <span className="text-[#FDFBF7]">{b.qty}× {b.name}</span>
                  <span className="text-[#A19D94]">{fmt(b.price * b.qty)}</span>
                </div>
              ))}
              <div className="flex justify-between px-5 py-3 border-t border-white/10">
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">Deposit already paid</span>
                <span className="text-sm text-green-300">{fmt(res.deposit_cents)}</span>
              </div>
              <div className="flex justify-between px-5 py-3">
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Balance due {res.balance_due_date}</span>
                <span className="font-serif-display text-xl text-[#F4D77E]" data-testid="balance-amount">{fmt(res.balance_due_cents)}</span>
              </div>
            </div>

            <a href={textHref} data-testid="balance-text-button"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.25em] border border-[#D4AF37] bg-[#D4AF37] text-black px-8 py-4 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300">
              <MessageSquare className="w-4 h-4" /> Text To Apple Pay {fmt(res.balance_due_cents)}
            </a>
            <p className="text-xs text-[#A19D94] mt-4 leading-relaxed" data-testid="balance-text-note">
              Text <span className="text-[#F4D77E]">{res.balance_phone || "(323) 926-1750"}</span> to Apple Pay your balance — mention order <span className="text-[#F4D77E]">{res.reservation_no}</span> so we can match it up.
            </p>
            <p className="text-[10px] text-[#5c5850] mt-3">Unpaid reservations are subject to cancellation. Admission tickets sold separately.</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
